/**
 * Sales-area route bundle — the order-desk workspace (orders, products,
 * product categories, inventory log, salespeople) for users with sales
 * access. Every page renders inside SalesProvider. Gated behind the
 * per-tenant EXCLUDE_SALES build flag so the area and its lazy chunks
 * drop out of a tenant build that does not license sales.
 *
 * Contains:
 * - salesRoutes — array of <Route> elements, or null when excluded
 */
import React from 'react';
import { Navigate, Route } from 'react-router-dom';

import SalesProvider from '@sophocles/providers/SalesProvider';
import { checkSalesAccess } from '@sophocles/router/checkCredentials';
import {
  salesHomePath,
  salesInventoryLogPath,
  salesOrderCreatePath,
  salesOrderPath,
  salesOrderPrintPath,
  salesOrdersPath,
  salesPeoplePath,
  salesProductCategoriesPath,
  salesProductCreatePath,
  salesProductPath,
  salesProductsPath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';

// --- Build-time DCE block ---
// Gated on the bare DefinePlugin global so webpack can prune the lazy()
// import() calls (and the resulting chunks) at build time when the flag
// is true.  Never extract the global into a variable — that would defeat
// tree-shaking.

export let salesRoutes = null;
if (!__EXCLUDE_SALES__) {
  const OrderListView = React.lazy(() => import('@sophocles/views/sales/OrderListView'));
  const OrderSaleFormView = React.lazy(() => import('@sophocles/views/sales/OrderSaleFormView'));
  const ProductCategoryManagement = React.lazy(() => import('@sophocles/views/sales/ProductCategoryManagementView'));
  const ProductFormView = React.lazy(() => import('@sophocles/views/sales/ProductFormView'));
  const PrintableOrderView = React.lazy(() => import('@sophocles/views/sales/PrintableOrderView'));
  const ProductInventoryLogView = React.lazy(() => import('@sophocles/views/sales/ProductInventoryLogView'));
  const ProductListView = React.lazy(() => import('@sophocles/views/sales/ProductListView'));
  const SalesPeopleView = React.lazy(() => import('@sophocles/views/sales/SalesPeopleView'));

  // Sales-section routes are wrapped in SalesProvider. Define the wrapped
  // components inside the guard so they only exist when the sales area is
  // included. They must be stable references (module-scope consts), so
  // we declare them here rather than inline in the JSX.
  const SalesOrderListPage = props => (
    <SalesProvider>
      <OrderListView {...props} />
    </SalesProvider>
  );
  const SalesProductInventoryLogPage = props => (
    <SalesProvider>
      <ProductInventoryLogView {...props} />
    </SalesProvider>
  );
  const SalesOrderSaleFormPage = props => (
    <SalesProvider>
      <OrderSaleFormView {...props} />
    </SalesProvider>
  );
  const SalesPrintableOrderPage = props => (
    <SalesProvider>
      <PrintableOrderView {...props} />
    </SalesProvider>
  );

  salesRoutes = [
    // /sales redirects to /sales/orders
    <Route
      key={salesHomePath.pattern}
      path={salesHomePath.pattern}
      element={<Navigate to={salesOrdersPath.pattern} replace />}
    />,
    <Route
      key={salesOrdersPath.pattern}
      path={salesOrdersPath.pattern}
      element={<RequireAuth component={SalesOrderListPage} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesPeoplePath.pattern}
      path={salesPeoplePath.pattern}
      element={<RequireAuth component={SalesPeopleView} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesProductsPath.pattern}
      path={salesProductsPath.pattern}
      element={<RequireAuth component={ProductListView} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesInventoryLogPath.pattern}
      path={salesInventoryLogPath.pattern}
      element={<RequireAuth component={SalesProductInventoryLogPage} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesProductCategoriesPath.pattern}
      path={salesProductCategoriesPath.pattern}
      element={<RequireAuth component={ProductCategoryManagement} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesProductCreatePath.pattern}
      path={salesProductCreatePath.pattern}
      element={<RequireAuth component={ProductFormView} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesProductPath.pattern}
      path={salesProductPath.pattern}
      element={<RequireAuth component={ProductFormView} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesOrderCreatePath.pattern}
      path={salesOrderCreatePath.pattern}
      element={<RequireAuth component={SalesOrderSaleFormPage} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesOrderPrintPath.pattern}
      path={salesOrderPrintPath.pattern}
      element={<RequireAuth component={SalesPrintableOrderPage} onEnter={checkSalesAccess} />}
    />,
    <Route
      key={salesOrderPath.pattern}
      path={salesOrderPath.pattern}
      element={<RequireAuth component={SalesOrderSaleFormPage} onEnter={checkSalesAccess} />}
    />,
  ];
}
